/**
 * Billing API - Falcon-specific types and methods
 *
 * Falcon has product-specific billing fields (max_apps, retention_days, etc.)
 * that aren't in the generic saas-shell types.
 */

import apiClient from './client'

// Falcon-specific Plan type
export interface PlanResponse {
  id: string
  name: string
  display_name: string
  description: string | null
  price_monthly: string
  stripe_price_id: string | null
  // Falcon-specific fields
  max_apps: number
  max_members: number
  max_api_keys: number
  retention_days: number
  max_errors_monthly: number
  // Display fields
  features: string[]
  badge: string | null
  recommended: boolean
}

// Falcon-specific Subscription type
export interface SubscriptionResponse {
  id: string
  status: string
  plan: PlanResponse | null
  current_period_start: string | null
  current_period_end: string | null
  cancel_at_period_end: boolean
  trial_end: string | null
}

// Falcon-specific Usage type
export interface UsageResponse {
  // Errors (primary usage metric)
  errors_this_month: number
  max_errors_monthly: number | null
  errors_remaining: number | null
  // Uptime monitors
  current_uptime_monitors: number
  max_uptime_monitors: number | null
  uptime_monitors_remaining: number | null
  // Cron monitors
  current_cron_monitors: number
  max_cron_monitors: number | null
  cron_monitors_remaining: number | null
  // Other metrics
  pings_this_month: number
  alerts_today: number
  max_alerts_daily: number | null
  alerts_remaining: number | null
  month_start: string
  check_interval_seconds: number
  retention_days: number
  // Apps (unlimited, kept for backwards compatibility)
  current_apps: number
  max_apps: number | null
  apps_remaining: number | null
}

// Checkout/Portal types
export interface CheckoutSessionResponse {
  session_id: string
  url: string
}

export interface PortalSessionResponse {
  url: string
}

/**
 * Falcon billing API - uses product-specific endpoints
 */
export const billingApi = {
  /**
   * Get available plans
   */
  getPlans: async (): Promise<PlanResponse[]> => {
    const response = await apiClient.get<PlanResponse[]>('/billing/plans')
    return response.data
  },

  /**
   * Get team plans (same as getPlans for now)
   */
  getTeamPlans: async (teamId: string): Promise<PlanResponse[]> => {
    const response = await apiClient.get<PlanResponse[]>(`/t/${teamId}/billing/plans`)
    return response.data
  },

  /**
   * Get team subscription
   */
  getSubscription: async (teamId?: string): Promise<SubscriptionResponse> => {
    const url = teamId ? `/t/${teamId}/billing/subscription` : '/billing/subscription'
    const response = await apiClient.get<SubscriptionResponse>(url)
    return response.data
  },

  /**
   * Get team usage
   */
  getUsage: async (teamId?: string): Promise<UsageResponse> => {
    const url = teamId ? `/t/${teamId}/billing/usage` : '/billing/usage'
    const response = await apiClient.get<UsageResponse>(url)
    return response.data
  },

  /**
   * Create a Stripe checkout session for a plan
   */
  createCheckoutSession: async (
    planName: string,
    teamId?: string
  ): Promise<CheckoutSessionResponse> => {
    const url = teamId
      ? `/t/${teamId}/billing/checkout?plan=${planName}`
      : `/billing/checkout?plan=${planName}`
    const response = await apiClient.post<CheckoutSessionResponse>(url)
    return response.data
  },

  /**
   * Create a Stripe billing portal session
   */
  createPortalSession: async (
    returnUrl?: string,
    teamId?: string
  ): Promise<PortalSessionResponse> => {
    const url = teamId ? `/t/${teamId}/billing/portal` : '/billing/portal'
    const response = await apiClient.post<PortalSessionResponse>(url, {
      return_url: returnUrl,
    })
    return response.data
  },

  /**
   * Switch plan (for already subscribed users)
   */
  switchPlan: async (
    teamId: string,
    planName: string
  ): Promise<{ switched: boolean; previous_plan: string; new_plan: string; message: string }> => {
    const response = await apiClient.post<{
      switched: boolean
      previous_plan: string
      new_plan: string
      message: string
    }>(`/t/${teamId}/billing/switch-plan`, {
      plan: planName,
    })
    return response.data
  },

  /**
   * Get portal URL (convenience wrapper around createPortalSession)
   */
  getPortalUrl: async (teamId: string): Promise<string> => {
    const response = await apiClient.post<PortalSessionResponse>(`/t/${teamId}/billing/portal`, {
      return_url: window.location.href,
    })
    return response.data.url
  },

  /**
   * Get checkout URL (convenience wrapper around createCheckoutSession)
   */
  getCheckoutUrl: async (teamId: string, planName: string): Promise<string> => {
    const response = await apiClient.post<{ url: string }>(
      `/t/${teamId}/billing/checkout?plan=${planName}`
    )
    return response.data.url
  },

  /**
   * Cancel subscription at period end
   */
  cancelSubscription: async (teamId: string): Promise<void> => {
    await apiClient.post(`/t/${teamId}/billing/cancel`)
  },
}
